import React, { createContext, useMemo, useEffect, useContext, FC, useState } from 'react';
import { OktaAuth, AuthState } from '@okta/okta-auth-js';
import config from './config'

export const OKTA_CONFIG = config.oidc;
type AuthContext = {
  authClient: OktaAuth;
};

export const AuthContext = createContext<AuthContext>(null as any);

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider: FC = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({ isAuthenticated: false, isPending: true });
  const authClient = useMemo(() => new OktaAuth(OKTA_CONFIG), []);
  console.log('auth authClient   ::::::::::     ', authClient)

  useEffect(() => {
    // * Subscribe with internal state is needed to trigger updates/redirect
    authClient.authStateManager.subscribe(setAuthState);

    // Update for initial render
    authClient.authStateManager.updateAuthState();

    // Check for changes to local storage every second
    const interval = window.setInterval(() => authClient.authStateManager.updateAuthState(), 1000);
    return () => clearInterval(interval);
  }, [authClient]);

  // Handle token retrieval
  useEffect(() => {
    const { isPending, isAuthenticated } = authState;
    // Ignore if pending or authenticated
    if (isPending || isAuthenticated) {
      return;
    }
    
    // Store tokens if sourced from redirect
    if (authClient.token.isLoginRedirect()) {
      authClient.storeTokensFromRedirect();
      return;
    }

    authClient.token.getWithRedirect();
  }, [authClient, authState]);

  // Handle user object
  useEffect(() => {
    const { isPending, isAuthenticated, idToken } = authState;
    if (isPending || !isAuthenticated || !idToken?.claims) {
      return;
    }

    // TODO? Retrieve user info from Auth context as opposed to Redux
    // dispatch.user.setUserAsync(idToken.claims);
    localStorage.setItem('Region', idToken?.claims.FranCountry)
    localStorage.setItem('BrandCode', idToken?.claims.BrandCode)
    localStorage.setItem('FzCode', idToken?.claims.franOrganizationId)
    localStorage.setItem('Role', idToken?.claims.franOrgRoles)
    localStorage.setItem('userType', idToken?.claims.UserType)

    // set dashboard user preferences in localstorage for now
    // TODO - integrate api for this
    let user = JSON.parse(localStorage.getItem('user'))
    if (!user || user.userId !== idToken?.claims.UserID) {
      user = { userId: idToken?.claims.UserID, isFirstTimeUser: true, preference: 'restaurants' }
    } else {
      user = { userId: idToken?.claims.UserID, isFirstTimeUser: false, preference: user.preference }
    }
    localStorage.setItem('user', JSON.stringify(user))

    if(idToken?.claims.BrandCode === 'BKC') {
      document.getElementById("favicon").setAttribute("href", "/assets/BKfavicon.ico")
    } else {
      document.getElementById("favicon").setAttribute("href", "/assets/PLKfavicon.ico")
    }
  }, [authClient, authState]);

  // Pending auth check / redirecting to login / fetching user
  if (authState.isPending || !authState.isAuthenticated) {
    return <h1>Loading.....</h1>
  }

  if (!authState.idToken?.claims) {
    // TODO! Consider i18n now that LocaleProvider is a descendent of AuthProvider
    return (
      <>
        Error fetching user
        <a href="/">Reload</a>
      </>
    );
  }

  return <AuthContext.Provider value={{ authClient }}>{children}</AuthContext.Provider>;
};
